/**
 * Double Row Template Generate Functions
 *
 * Generates building and unit configurations from parameters.
 * Creates two rows of units sharing a spine wall (back-to-back).
 */

import type { GeneratedBuilding, GeneratedUnit, DoorConfig, DoorSpec, DoorWall } from '../types'
import type { DoubleRowParams } from './params'
import {
  getEffectiveRowB,
  calculateBuildingDepth,
  getUnitDoors,
  generateUnitCode,
} from './params'
import {
  calculateUnitWidthsWithOverrides,
  calculateUnitOffsets,
} from '../base'
import { createDoubleRowEdges } from './edges'

/**
 * Generate building configuration from double-row parameters
 */
export function generateBuilding(params: DoubleRowParams): GeneratedBuilding {
  // Convert bearing from degrees to radians
  const rotationRadians = (params.bearing_deg * Math.PI) / 180

  // Get effective row B (mirrors row A in symmetric mode)
  const rowB = getEffectiveRowB(params)

  // Calculate total building depth (Y-axis)
  const buildingDepth = calculateBuildingDepth(params)

  // Calculate unit widths for each row
  const rowAWidths = calculateUnitWidthsWithOverrides(
    params.width_ft,
    params.row_a.unit_count,
    params.partition_wall_thickness_ft,
    params.row_a_overrides?.filter(o => o.width_ft !== undefined).map(o => ({
      index: o.index,
      width_ft: o.width_ft!,
    }))
  )

  const rowBWidths = calculateUnitWidthsWithOverrides(
    params.width_ft,
    rowB.unit_count,
    params.partition_wall_thickness_ft,
    params.row_b_overrides?.filter(o => o.width_ft !== undefined).map(o => ({
      index: o.index,
      width_ft: o.width_ft!,
    }))
  )

  // Build door configs for all units (all walls)
  const doorConfigs: DoorConfig[] = []

  // Row A offsets (units along X-axis)
  const rowAOffsets = calculateUnitOffsets(rowAWidths.widths, params.partition_wall_thickness_ft)

  // Row B offsets
  const rowBOffsets = calculateUnitOffsets(rowBWidths.widths, params.partition_wall_thickness_ft)

  // Helper to add doors from a wall
  const addDoorsFromWall = (
    wall: DoorWall,
    doors: DoorSpec[] | undefined,
    unitOffsetX: number,
    _unitOffsetY: number,
    unitWidth: number,
    _unitDepth: number,
    _isRowB: boolean = false
  ) => {
    if (!doors) return

    for (const door of doors) {
      let absoluteOffsetX: number

      // Calculate absolute position based on wall and unit position
      if (wall === 'front') {
        // Front wall: along X-axis at front of unit
        absoluteOffsetX = unitOffsetX + door.offset_ft
      } else if (wall === 'back') {
        // Back wall (spine): along X-axis at back of unit
        absoluteOffsetX = unitOffsetX + door.offset_ft
      } else if (wall === 'left') {
        // Left wall: along Y-axis
        absoluteOffsetX = unitOffsetX + unitWidth
      } else {
        // Right wall: along Y-axis
        absoluteOffsetX = unitOffsetX
      }

      doorConfigs.push({
        wall,
        width_ft: door.width_ft,
        height_ft: door.height_ft,
        offset_ft: absoluteOffsetX, // Store X offset for now (simplified)
        type: door.type,
      })
    }
  }

  // Process Row A units (north side)
  const rowAOffsetY = rowB.depth_ft + params.shared_wall_thickness_ft
  for (let i = 0; i < params.row_a.unit_count; i++) {
    const unitWidth = rowAWidths.widths[i]
    const unitDoors = getUnitDoors(params, 'A', i, unitWidth, params.row_a.unit_count)

    addDoorsFromWall('front', unitDoors.front, rowAOffsets[i], rowAOffsetY, unitWidth, params.row_a.depth_ft, false)
    addDoorsFromWall('back', unitDoors.back, rowAOffsets[i], rowAOffsetY, unitWidth, params.row_a.depth_ft, false)
    addDoorsFromWall('left', unitDoors.left, rowAOffsets[i], rowAOffsetY, unitWidth, params.row_a.depth_ft, false)
    addDoorsFromWall('right', unitDoors.right, rowAOffsets[i], rowAOffsetY, unitWidth, params.row_a.depth_ft, false)
  }

  // Process Row B units (south side)
  for (let i = 0; i < rowB.unit_count; i++) {
    const unitWidth = rowBWidths.widths[i]
    const unitDoors = getUnitDoors(params, 'B', i, unitWidth, rowB.unit_count)

    addDoorsFromWall('front', unitDoors.front, rowBOffsets[i], 0, unitWidth, rowB.depth_ft, true)
    addDoorsFromWall('back', unitDoors.back, rowBOffsets[i], 0, unitWidth, rowB.depth_ft, true)
    addDoorsFromWall('left', unitDoors.left, rowBOffsets[i], 0, unitWidth, rowB.depth_ft, true)
    addDoorsFromWall('right', unitDoors.right, rowBOffsets[i], 0, unitWidth, rowB.depth_ft, true)
  }

  return {
    name: params.name,
    code: params.code,
    type: 'box_hangar_double_row',
    exterior_width: params.width_ft, // X-axis (door-parallel)
    exterior_depth: buildingDepth, // Y-axis (total depth = row_a + row_b + spine)
    exterior_height: params.height_ft,
    latitude: params.latitude,
    longitude: params.longitude,
    rotation: rotationRadians,
    door_width: params.default_door_width_ft, // Primary door width
    door_height: params.default_door_height_ft,
    settings: {
      floor_color: params.floor_color,
      wall_color: params.wall_color,
      doors: doorConfigs,
    },
    type_config: {
      template: 'double_row',
      layout_mode: params.layout_mode,
      row_a: params.row_a,
      row_b: getEffectiveRowB(params),
      shared_wall_thickness_ft: params.shared_wall_thickness_ft,
      partition_wall_thickness_ft: params.partition_wall_thickness_ft,
      numbering_style: params.numbering_style,
      // Default door settings (for form reload)
      default_door_type: params.default_door_type,
      default_door_width_ft: params.default_door_width_ft,
      default_door_height_ft: params.default_door_height_ft,
      // Per-unit overrides (for form reload - contains custom doors per unit)
      row_a_overrides: params.row_a_overrides,
      row_b_overrides: params.row_b_overrides,
      // Calculated values
      calculated_row_a_widths: rowAWidths.widths,
      calculated_row_b_widths: rowBWidths.widths,
    },
  }
}

/**
 * Generate unit configurations from building and parameters
 */
export function generateUnits(
  _building: GeneratedBuilding,
  params: DoubleRowParams
): GeneratedUnit[] {
  const units: GeneratedUnit[] = []

  // Get effective row B
  const rowB = getEffectiveRowB(params)

  // Calculate unit widths for each row
  const rowAWidths = calculateUnitWidthsWithOverrides(
    params.width_ft,
    params.row_a.unit_count,
    params.partition_wall_thickness_ft,
    params.row_a_overrides?.filter(o => o.width_ft !== undefined).map(o => ({
      index: o.index,
      width_ft: o.width_ft!,
    }))
  )

  const rowBWidths = calculateUnitWidthsWithOverrides(
    params.width_ft,
    rowB.unit_count,
    params.partition_wall_thickness_ft,
    params.row_b_overrides?.filter(o => o.width_ft !== undefined).map(o => ({
      index: o.index,
      width_ft: o.width_ft!,
    }))
  )

  // Calculate unit offsets
  const rowAOffsets = calculateUnitOffsets(rowAWidths.widths, params.partition_wall_thickness_ft)
  const rowBOffsets = calculateUnitOffsets(rowBWidths.widths, params.partition_wall_thickness_ft)

  // Interior depths for double-row units
  // Note: partition_wall_thickness is for side walls (X-axis) between units, not the spine wall (Y-axis)
  // Each row's full depth is used as interior depth; the spine wall is separate
  const rowAInteriorDepth = params.row_a.depth_ft
  const rowBInteriorDepth = rowB.depth_ft

  // Y offsets for each row
  // Units are positioned at their CENTER in 3D space, with walls extending symmetrically
  // offset_y must position units so they occupy the correct Y range in the building
  //
  // Row A: FRONT row (closer to camera), occupies Y=0 to Y=row_a.depth_ft
  // Row B: BACK row (farther from camera), starts after Row A + shared wall
  const rowAOffsetY = params.row_a.depth_ft / 2  // Row A at front (smaller offset_y)
  const rowBOffsetY = params.row_a.depth_ft + params.shared_wall_thickness_ft + (rowB.depth_ft / 2)  // Row B at back

  // Generate Row A units (north side - doors face front/north)
  for (let i = 0; i < params.row_a.unit_count; i++) {
    const unitWidth = rowAWidths.widths[i]
    const unitDoors = getUnitDoors(params, 'A', i, unitWidth, params.row_a.unit_count)
    const unitCode = generateUnitCode(params, 'A', i, 0)

    // Get the first front door's dimensions for unit-level storage
    const primaryDoor = unitDoors.front?.[0]

    // Check for type override on this unit
    const typeOverride = params.row_a_overrides?.find(o => o.index === i)?.type

    // Create edge classification with per-wall doors
    const edges = createDoubleRowEdges(
      i,
      params.row_a.unit_count,
      'A',
      params.code,
      unitDoors
    )

    const unit: GeneratedUnit = {
      code: unitCode,
      type: typeOverride || 'box_hangar',
      capacity_strategy: 'single',
      interior_width: unitWidth,
      interior_depth: rowAInteriorDepth,
      interior_height: params.height_ft,
      door_width: primaryDoor?.width_ft ?? null,
      door_height: primaryDoor?.height_ft ?? null,
      door_type: primaryDoor?.type,
      offset_x: rowAOffsets[i],
      offset_y: rowAOffsetY,
      unit_rotation: 0, // Row A faces front (no rotation)
      edges,
    }

    units.push(unit)
  }

  // Generate Row B units (south side - doors face front/south, rotated 180°)
  for (let i = 0; i < rowB.unit_count; i++) {
    const unitWidth = rowBWidths.widths[i]
    const unitDoors = getUnitDoors(params, 'B', i, unitWidth, rowB.unit_count)
    const unitCode = generateUnitCode(params, 'B', i, params.row_a.unit_count)

    // Get the first front door's dimensions for unit-level storage
    const primaryDoor = unitDoors.front?.[0]

    // Check for type override on this unit
    const typeOverride = params.row_b_overrides?.find(o => o.index === i)?.type

    // Create edge classification with per-wall doors
    const edges = createDoubleRowEdges(
      i,
      rowB.unit_count,
      'B',
      params.code,
      unitDoors
    )

    const unit: GeneratedUnit = {
      code: unitCode,
      type: typeOverride || 'box_hangar',
      capacity_strategy: 'single',
      interior_width: unitWidth,
      interior_depth: rowBInteriorDepth,
      interior_height: params.height_ft,
      door_width: primaryDoor?.width_ft ?? null,
      door_height: primaryDoor?.height_ft ?? null,
      door_type: primaryDoor?.type,
      offset_x: rowBOffsets[i],
      offset_y: rowBOffsetY,
      unit_rotation: Math.PI, // Row B faces opposite direction (180°)
      edges,
    }

    units.push(unit)
  }

  return units
}
